<template>
  <div class="companies-table content-box" data-cy="companies-table">
    <div
      class="companies-table-header grid-control__container table-modifiers clearfix"
    >
      <TableSearch
        class="company-search"
        :value="searchInput"
        placeholder="Search by Company or RC Name"
        @searchSubmitted="onSearchSubmitted"
        data-cy="company-search"
      />
      <ClearFiltersButton
        class="pull-right"
        @clearFiltersClicked="clearFilters"
        data-cy="clear-filters"
      />
    </div>
    <CompaniesGrid
      :companies="companies"
      :companiesLoading="companiesLoading"
      :pageSize="companiesTake"
      :skip="companiesSkip"
      :totalCompanies="totalCompanies"
      :filter="companiesFilter"
      :getPropValues="getCompanyPropValuesAsync"
      @companiesChanged="onCompaniesChanged"
      @sortChanged="onSortChanged"
    />
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import CompaniesGrid from "./CompaniesGrid.vue";
import { useMainStore } from "@/stores/useMainStore";
import { onBeforeMount, ref, watchEffect } from "vue";
import TableSearch from "@/shared/ui/inputs/TableSearch.vue";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { getJsonFilterString } from "@/shared/helpers/filtersHelper";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import ClearFiltersButton from "@/shared/ui/inputs/ClearFiltersButton.vue";
import type { CompanyDisplay } from "@/Domain/CompanyDisplay";
import type { CompositeFilterDescriptor } from "@progress/kendo-data-query";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import type { GetCompaniesWithCertifiedProductsWithFilteringRequest } from "@/api/ukdCompanies";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import { GridOptionsService } from "@/shared/services/GridOptionsService";

const main = useMainStore();
const searchInput = ref<string>(main.companyRcFilterCriteria);
const companies = ref<CompanyDisplay[]>([]);
const totalCompanies = ref<number>(0);
const companiesLoading = ref<boolean>(false);

const companiesFilter = ref<CompositeFilterDescriptor | undefined>(undefined);
const sortField = ref<string>("");
const sortAscending = ref<boolean>(false);

const companiesTake = ref<number>(25);
const companiesSkip = ref<number>(0);

const tableName = "CompaniesRcTable";

const getCompaniesWithCertifiedProductsAsync = async function (
  searchString: string,
  skip: number,
  take: number,
  filter: CompositeFilterDescriptor | null = null,
  valuesForFieldOnly: string = ""
) {
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    name: searchString,
    filters: getJsonFilterString(filter),
    skip: skip,
    take: take,
    includeConfidentialAndOnHold: true,
    includeRC: true,
    sortField: sortField.value,
    ascending: sortAscending.value,
    valuesForFieldOnly: valuesForFieldOnly,
  } as GetCompaniesWithCertifiedProductsWithFilteringRequest;

  if (!valuesForFieldOnly)
    return api.get(
      "GetCompaniesWithCertifiedProductsWithFiltering",
      requestParams
    );

  requestParams.take = 1000;
  return CacheService.getAsync(createCacheKey(requestParams), () =>
    api.get("GetCompaniesWithCertifiedProductsWithFiltering", requestParams)
  );
};

const clearFilters = function () {
  searchInput.value = "";
  companiesFilter.value = undefined;
  sortField.value = "";
  sortAscending.value = false;
  companiesSkip.value = 0;
  searchCompaniesWithCertifiedProductsAsync();
};

const onSearchSubmitted = function (searchValue: string) {
  searchInput.value = searchValue;
  companiesSkip.value = 0;
  searchCompaniesWithCertifiedProductsAsync(companiesFilter.value);
};

const onCompaniesChanged = function (options: ItemChangedOptions) {
  if (options.filterEvent) {
    companiesFilter.value = options.filterEvent.filter;
  }
  if (companiesTake.value != options.take) {
    companiesTake.value = options.take;
    GridOptionsService.savePageSizeAsync(tableName, options.take);
  }
  companiesSkip.value = options.skip;
  searchCompaniesWithCertifiedProductsAsync(companiesFilter.value);
};

const onSortChanged = function (options: ItemSortChangedOptions) {
  sortField.value = options.field;
  sortAscending.value = options.ascending;
  companiesSkip.value = 0;
  searchCompaniesWithCertifiedProductsAsync(companiesFilter.value);
};

const searchCompaniesWithCertifiedProductsAsync = async function (
  filter: CompositeFilterDescriptor | null = null
) {
  if (companiesLoading.value) return;

  companiesLoading.value = true;

  await handleResponsePromiseAsync(
    getCompaniesWithCertifiedProductsAsync(
      searchInput.value,
      companiesSkip.value,
      companiesTake.value,
      filter
    ),
    (data) => {
      companies.value = data.ListItems;
      totalCompanies.value = data.TotalItemsInList;
    },
    "CompaniesTable.vue",
    "searchCompaniesWithCertifiedProductsAsync"
  );

  companiesLoading.value = false;
};

const getCompanyPropValuesAsync = async function (
  column: GridColumnPropsExtended
) {
  let result: CompanyDisplay[] = [];
  await handleResponsePromiseAsync(
    getCompaniesWithCertifiedProductsAsync(
      searchInput.value,
      0,
      999999,
      null,
      column.field
    ),
    (data) => {
      result = data.ListItems;
    },
    "CompaniesTable.vue:",
    "getCompanyPropValuesAsync"
  );
  return result;
};

watchEffect(() => {
  console.debug(
    "CompaniesTable.vue, searchInput watch updated:",
    searchInput.value
  );
  main.companyRcFilterCriteria = searchInput.value;
});

onBeforeMount(async () => {
  await searchCompaniesWithCertifiedProductsAsync();
});
</script>

<style>
.company-search {
  width: 400px;
}
</style>
